// src/AdminMembershipTab.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminMembershipTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchMembershipAnalytics();
  }, []);

  const fetchMembershipAnalytics = async () => {
    try {
      setLoading(true);
      const response = await authedFetch(`${apiUrl('adminAnalytics')}?type=membership`);
      const result = await response.json();
      
      if (result.success) {
        setAnalytics(result.data);
        setLastUpdated(new Date(result.timestamp));
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch membership analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Loading membership analytics...</div>;
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <p>Error: {error}</p>
        <button onClick={fetchMembershipAnalytics} style={refreshButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Membership Analytics</h2>
        <div style={refreshContainerStyle}>
          <button onClick={fetchMembershipAnalytics} style={refreshButtonStyle}>
            Refresh Data
          </button>
          {lastUpdated && (
            <span style={timestampStyle}>
              Last updated: {lastUpdated.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Account Status Breakdown */}
      <div style={sectionStyle}>
        <h3>Account Status Breakdown</h3>
        <div style={statusGridStyle}>
          <div style={statusCardStyle}>
            <div style={statusLabelStyle}>Active</div>
            <div style={{...statusValueStyle, color: '#22c55e'}}>{analytics.statusBreakdown.active}</div>
          </div>
          <div style={statusCardStyle}>
            <div style={statusLabelStyle}>Paused</div>
            <div style={{...statusValueStyle, color: '#f59e0b'}}>{analytics.statusBreakdown.paused}</div>
          </div>
          <div style={statusCardStyle}>
            <div style={statusLabelStyle}>Suspended</div>
            <div style={{...statusValueStyle, color: '#ef4444'}}>{analytics.statusBreakdown.suspended}</div>
          </div>
          <div style={statusCardStyle}>
            <div style={statusLabelStyle}>Payment Failed</div>
            <div style={{...statusValueStyle, color: '#dc2626'}}>{analytics.statusBreakdown.paymentFailed}</div>
          </div>
          <div style={statusCardStyle}>
            <div style={statusLabelStyle}>Deleted</div>
            <div style={{...statusValueStyle, color: '#6b7280'}}>{analytics.statusBreakdown.deleted}</div>
          </div>
        </div>
      </div>

      {/* Communication Preferences */}
      <div style={sectionStyle}>
        <h3>Communication Preferences</h3>
        <div style={prefsGridStyle}>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>📧📱</div>
            <div style={prefLabelStyle}>Both Email & SMS</div>
            <div style={prefValueStyle}>{analytics.communicationPrefs.both}</div>
          </div>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>📧</div>
            <div style={prefLabelStyle}>Email Only</div>
            <div style={prefValueStyle}>{analytics.communicationPrefs.email}</div>
          </div>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>📱</div>
            <div style={prefLabelStyle}>SMS Only</div>
            <div style={prefValueStyle}>{analytics.communicationPrefs.text}</div>
          </div>
        </div>
      </div>

      {/* Game Preferences */}
      <div style={sectionStyle}>
        <h3>Game Preferences</h3>
        <div style={prefsGridStyle}>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>🎯</div>
            <div style={prefLabelStyle}>Both Games</div>
            <div style={prefValueStyle}>{analytics.gamePrefs.both}</div>
          </div>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>🎲</div>
            <div style={prefLabelStyle}>Powerball Only</div>
            <div style={prefValueStyle}>{analytics.gamePrefs.powerball}</div>
          </div>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>💎</div>
            <div style={prefLabelStyle}>Mega Millions Only</div>
            <div style={prefValueStyle}>{analytics.gamePrefs.megamillions}</div>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div style={sectionStyle}>
        <h3>Top States</h3>
        <div style={geoListStyle}>
          {analytics.topStates.map((state, index) => (
            <div key={state.state} style={geoItemStyle}>
              <span style={geoRankStyle}>{index + 1}</span>
              <span style={geoStateStyle}>{state.state}</span>
              <span style={geoCountStyle}>{state.count} members</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
  paddingBottom: '1rem',
  borderBottom: '2px solid #e5e7eb'
};

const refreshContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const refreshButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#1976d2',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer'
};

const timestampStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const sectionStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const statusGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '1rem',
  marginTop: '1rem'
};

const statusCardStyle = {
  textAlign: 'center',
  padding: '1rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px'
};

const statusLabelStyle = {
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#666',
  marginBottom: '0.5rem'
};

const statusValueStyle = {
  fontSize: '1.8rem',
  fontWeight: 'bold'
};

const prefsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1.5rem',
  marginTop: '1rem'
};

const prefCardStyle = {
  textAlign: 'center',
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px'
};

const prefIconStyle = {
  fontSize: '2rem',
  marginBottom: '0.5rem'
};

const prefLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#666',
  marginBottom: '0.5rem'
};

const prefValueStyle = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1976d2'
};

const geoListStyle = {
  marginTop: '1rem'
};

const geoItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px',
  marginBottom: '0.5rem'
};

const geoRankStyle = {
  width: '30px',
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#666'
};

const geoStateStyle = {
  flex: 1,
  fontSize: '1rem',
  fontWeight: '600'
};

const geoCountStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const loadingStyle = {
  padding: '2rem',
  textAlign: 'center',
  fontSize: '1.2rem'
};

const errorStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: '#d32f2f'
};

export default AdminMembershipTab;